/**
 * UpdateActionCard - v2 content for CRM property update actions.
 *
 * Renders a EntityCard header showing the entity being updated, then a
 * DiffSection below showing current → proposed values for all properties
 * including stage/lifecycle transitions.
 *
 * Values render through the canonical CRM display components (via
 * ProposalPropertyValue) so a property looks the same here as it does in a
 * list cell or a slideout, and long values expand in place instead of
 * hard-truncating.
 *
 * Clicking a proposed value opens its input in place and hands rendering to
 * UpdateActionEditForm, which autosaves. There is no pencil and no Save button
 * for this action type (see `isEditableAction`) — the edit persists on commit,
 * so a second, shell-driven edit session would only add a dirty flag that
 * could disagree with the autosave about whether anything is outstanding.
 *
 * The edit form mounts lazily on that first click and then stays mounted: it
 * owns the draft and the pending save, and mounting it up-front would fire the
 * pipeline-stages and association-type fetches on every proposal page load.
 *
 * Shell is provided by the parent.
 */

import { useCallback, useMemo, useState } from 'react';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { useCrmEntityProps } from '@/lib/crm/object-cards/useCrmEntityProps';

import type {
  PropertyUpdateEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isPropertyUpdatePayload } from '../../lib/proposals/core/types';
import { useProposalSelectionOptional } from '../../lib/proposals/hooks/ProposalSelectionContext';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { ActionEntityPreview } from './ActionEntityPreview';
import { extractProperties } from './CreateActionCard.helpers';
import { type DiffRow, DiffSection } from './DiffSection';
import {
  DIFF_VALUE_MAX_LENGTH,
  ProposalPropertyValue,
} from './ProposalPropertyValue';
import {
  ActionCardContent,
  ActionError,
  extractValue,
  formatPropertyLabel,
} from './shared';
import { UpdateActionEditForm } from './UpdateActionEditForm';
import {
  isStageProperty,
  propertyValueTextLength,
  resolveCurrentValue,
} from './UpdateActionEditForm.helpers';
import { useCurrentStageValue } from './useCurrentStageValue';

/** Stable empty objects to avoid re-renders when payload is invalid */
const EMPTY_PROPERTIES: Record<string, { value: unknown }> = {};
const EMPTY_ORIGINAL_VALUES: Record<string, unknown> = {};

// ─── Component ───────────────────────────────────────────────────────────────

export function UpdateActionCard({
  action,
  mode,
  enrichedData,
  proposalId,
  orgId,
  onUpdate,
  isProcessing = false,
}: ActionRendererProps) {
  // `onUpdate` is only supplied when the registry considers this action
  // editable in this mode (see ActionRenderer), so it doubles as the
  // "may the user change this?" gate — the same signal the other autosaving
  // cards use.
  const isEditable = mode === 'full' && !!onUpdate && !isProcessing;
  const isValid = isPropertyUpdatePayload(action.payload);

  // Cast upfront — guarded by isValid check below (early return before any usage)
  const typedAction = action as ProposedAction<PropertyUpdateEntityPayload>;
  const entity_type_name = isValid ? typedAction.payload.entity_type_name : '';
  const entity_id = isValid ? typedAction.payload.entity_id : '';
  const crmEntityId = entity_id ? entity_id : undefined;
  const proposedProperties = isValid
    ? typedAction.payload.payload.properties
    : EMPTY_PROPERTIES;
  const originalValues = isValid
    ? (typedAction.payload.payload.original_values ?? EMPTY_ORIGINAL_VALUES)
    : EMPTY_ORIGINAL_VALUES;

  const entityType = enrichedData?.entity_type_name || entity_type_name;
  const entityLabel =
    enrichedData?.object_label || `Updated ${entity_type_name}`;

  // Fetch + subscribe to live CRM data for the DiffSection's "current"
  // column. Only used for the per-property fallback chain below (not for
  // the entity preview header — that goes through ActionEntityPreview).
  const crmFlatProps = useCrmEntityProps(orgId ?? 0, crmEntityId);

  // Property-type schemas drive type-aware rendering (select labels, url
  // links, currency, dates). This shares ONE React Query entry per
  // org+entity-type with every other consumer — the edit form, the CRM list,
  // the record view — and is seeded synchronously from localStorage, so a
  // proposal with many update cards does not fetch per card.
  const { schema } = usePropertySchema(orgId ?? '', entity_type_name);

  // Identify stage property keys so we can look up the current stage from
  // the CRM entity's associations (stages are associations, not properties).
  const propertyKeys = useMemo(
    () => Object.keys(proposedProperties),
    [proposedProperties]
  );
  const stagePropertyKeys = useMemo(
    () => propertyKeys.filter(isStageProperty),
    [propertyKeys]
  );

  // Look up the entity's current stage from CRM store associations.
  // useCrmEntityProps only returns properties — stages need association lookup.
  const currentStageValues = useCurrentStageValue(
    orgId,
    crmEntityId,
    stagePropertyKeys,
    entity_type_name
  );

  // Live-first no-op detection for per-row filtering (see
  // ProposalSelectionContext's `getNoOpPropertyKeys`, backed by
  // `getNoOpPropertyKeysLive`). Fails open to `[]` when there's no
  // ProposalSelectionProvider in the tree (e.g. an isolated card render) —
  // every row renders.
  const selection = useProposalSelectionOptional();
  const noOpKeys = useMemo(
    () =>
      isValid
        ? new Set(selection?.getNoOpPropertyKeys(action.id) ?? [])
        : new Set<string>(),
    [isValid, selection, action.id]
  );

  // Field the user clicked. Non-null once the first click happens, which is
  // also what mounts the edit form (and keeps it mounted — it owns the draft
  // and any in-flight autosave).
  const [pendingEditKey, setPendingEditKey] = useState<string | null>(null);

  const beginEdit = useCallback((key: string) => {
    setPendingEditKey(key);
  }, []);

  // When the entity isn't in CRM yet, ActionEntityPreview's store fetch
  // returns nothing. Provide fallback flatProps from enrichedData /
  // proposed properties so the preview card can still render details.
  const fallbackFlatProps = useMemo(() => {
    if (crmEntityId) {
      return undefined;
    }
    if (enrichedData?.current_values) {
      return extractProperties(enrichedData.current_values);
    }
    return extractProperties(proposedProperties);
  }, [crmEntityId, enrichedData?.current_values, proposedProperties]);

  // Build diff rows for all property updates (including stage transitions).
  // Current value precedence is owned by resolveCurrentValue so the read card
  // and the edit form can never disagree about what "current" means.
  const currentValues = enrichedData?.current_values;

  const diffRows: DiffRow[] = useMemo(
    () =>
      propertyKeys
        .filter(key => !noOpKeys.has(key))
        .map(key => {
          const currentRaw = resolveCurrentValue(key, {
            originalValues,
            currentValues,
            crmFlatProps,
            currentStageValues,
          });
          const proposedRaw = extractValue(proposedProperties[key]);
          const propertyType = schema[key];
          const label = propertyType?.label || formatPropertyLabel(key);

          return {
            id: key,
            label,
            multiline: true,
            isExpandable:
              propertyValueTextLength(
                currentRaw,
                propertyType?.field_type_name
              ) > DIFF_VALUE_MAX_LENGTH ||
              propertyValueTextLength(
                proposedRaw,
                propertyType?.field_type_name
              ) > DIFF_VALUE_MAX_LENGTH,
            currentValue: (
              <ProposalPropertyValue
                propertyKey={key}
                value={currentRaw}
                propertyType={propertyType}
                orgId={orgId}
              />
            ),
            proposedValue: (
              <ProposalPropertyValue
                propertyKey={key}
                value={proposedRaw}
                propertyType={propertyType}
                orgId={orgId}
              />
            ),
            onEditProposed: isEditable ? () => beginEdit(key) : undefined,
            editLabel: `Edit ${label}`,
          };
        }),
    [
      propertyKeys,
      noOpKeys,
      originalValues,
      currentValues,
      crmFlatProps,
      currentStageValues,
      proposedProperties,
      schema,
      orgId,
      isEditable,
      beginEdit,
    ]
  );

  // Entity preview header. Delegating to ActionEntityPreview keeps the
  // rendering path consistent with every other v2 action card: registry
  // lookup, CrmClickableContent for slideout-on-click, EntityCard fallback
  // for unknown types, and — critically — a loading skeleton while the CRM
  // store hydrates (covers both the source entity and its associations,
  // e.g. the nested company sub-card on a deal). Previously UpdateActionCard
  // rendered ContentComponent directly with partial fallback data, which
  // produced the "fields pop in one-by-one" flash that the rest of the
  // card types already fixed via ActionEntityPreview.
  const renderedContent = (
    <ActionEntityPreview
      entityId={crmEntityId}
      entityType={entityType}
      label={entityLabel}
      enrichedData={enrichedData}
      orgId={orgId}
      fallbackFlatProps={fallbackFlatProps}
    />
  );

  if (!isValid) {
    return (
      <ActionError message='Invalid property update payload' mode={mode} />
    );
  }

  return (
    <ActionCardContent>
      {renderedContent}
      {typedAction.payload.context_message && (
        <MarkdownContent
          className='text-sm text-fg-secondary'
          content={typedAction.payload.context_message}
          orgId={orgId}
          size='compact'
          resolvedSlots={typedAction.enriched_data?.resolved_slots}
        />
      )}
      {pendingEditKey === null ? (
        <DiffSection rows={diffRows} />
      ) : (
        <UpdateActionEditForm
          action={typedAction}
          enrichedData={enrichedData}
          proposalId={proposalId}
          orgId={orgId}
          onUpdate={onUpdate}
          initialEditKey={pendingEditKey}
        />
      )}
    </ActionCardContent>
  );
}
